import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Layout from '../Components/Layout';
import { fetchAppEvents, fetchAppEventsAggregate } from '../redux/actions/eventsActions';
import useCallOnce from '../hooks/useCallOnce';
import { Box, Typography, Paper, Grid, Button, TextField, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, IconButton, Chip } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export default function Events() {
  const dispatch = useDispatch();
  const { listLoading, listError, events, nextCursor, hasMore, aggregateLoading, aggregateError, aggregate } = useSelector((s) => s.events);

  const formatYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const today = formatYMD(new Date());

  // const [dateRange, setDateRange] = useState({ startDate: today, endDate: today });
  const [cursor, setCursor] = useState('');
  const [limit] = useState(100);
  const [pages, setPages] = useState([]); // { cursor, events, nextCursor }
  const [pageIndex, setPageIndex] = useState(0);

  useCallOnce('events:init', () => {
    (async () => {
      const data = await dispatch(fetchAppEvents({ limit: 100, cursor: '' /*, startDate: today, endDate: today*/ }));
      const payload = data?.data || data || {};
      const firstEvents = Array.isArray(payload?.events) ? payload.events : [];
      const nextCur = payload?.pagination?.nextCursor || '';
      setPages([{ cursor: '', events: firstEvents, nextCursor: nextCur }]);
      setPageIndex(0);
      await dispatch(fetchAppEventsAggregate({ /*startDate: today, endDate: today*/ }));
    })();
  }, []);

  const handleRefresh = async () => {
    setCursor('');
    const data = await dispatch(fetchAppEvents({ limit: 100, cursor: '' /*, startDate: dateRange.startDate, endDate: dateRange.endDate*/ }));
    const payload = data?.data || data || {};
    const firstEvents = Array.isArray(payload?.events) ? payload.events : [];
    const nextCur = payload?.pagination?.nextCursor || '';
    setPages([{ cursor: '', events: firstEvents, nextCursor: nextCur }]);
    setPageIndex(0);
    await dispatch(fetchAppEventsAggregate({ /*startDate: dateRange.startDate, endDate: dateRange.endDate*/ }));
  };

  const handleNextPage = async () => {
    const current = pages[pageIndex] || { nextCursor };
    // If we already have the next page cached, move forward without fetching
    if (pageIndex < pages.length - 1) {
      setPageIndex((i) => i + 1);
      setCursor(pages[pageIndex + 1]?.cursor || '');
      return;
    }
    // Otherwise fetch next using nextCursor from current page
    const newCursor = current?.nextCursor || '';
    if (!hasMore || !newCursor) return;
    setCursor(newCursor);
    const data = await dispatch(fetchAppEvents({ limit: 100, cursor: newCursor /*, startDate: dateRange.startDate, endDate: dateRange.endDate*/ }));
    const payload = data?.data || data || {};
    const newEvents = Array.isArray(payload?.events) ? payload.events : [];
    const nextCur = payload?.pagination?.nextCursor || '';
    setPages((prev) => [...prev, { cursor: newCursor, events: newEvents, nextCursor: nextCur }]);
    setPageIndex((i) => i + 1);
  };

  const handlePrevPage = () => {
    if (pageIndex <= 0) return;
    const prevIndex = pageIndex - 1;
    setPageIndex(prevIndex);
    setCursor(pages[prevIndex]?.cursor || '');
  };

  const aggregatePretty = useMemo(() => {
    try { return JSON.stringify(aggregate, null, 2); } catch (_) { return String(aggregate || ''); }
  }, [aggregate]);

  const totalInstalls = (() => {
    const data = aggregate?.data || aggregate;
    return Number(data?.totalInstalls || 0);
  })();
  const totalUninstalls = (() => {
    const data = aggregate?.data || aggregate;
    return Number(data?.totalUninstalls || 0);
  })();

  const formatReadableDateTime = (value) => {
    if (!value) return '—';
    try {
      const d = new Date(value);
      return d.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: true,
      });
    } catch (_) {
      return String(value);
    }
  };

  // UI Date Field like Brands page
  const UiDateField = ({ label, value, onChange, min, max }) => {
    const hiddenRef = React.useRef(null);
    const openPicker = () => {
      const el = hiddenRef.current;
      if (!el) return;
      try { el.focus(); } catch (_) {}
      if (typeof el.showPicker === 'function') {
        try { el.showPicker(); } catch (_) {}
      } else {
        try { el.click(); } catch (_) {}
      }
    };
    const mmddyyyy = (iso) => {
      if (!iso) return '';
      const [y,m,d] = iso.split('-');
      if (!y||!m||!d) return '';
      return `${m}/${d}/${y}`;
    };
    return (
      <Box sx={{ position: 'relative' }} onMouseDown={(e) => { e.preventDefault(); openPicker(); }}>
        <TextField fullWidth label={label} type="text" placeholder="MM/DD/YYYY" value={mmddyyyy(value)} onFocus={openPicker} size="small" InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }} inputProps={{ readOnly: true }} />
        <input ref={hiddenRef} type="date" value={value || ''} min={min} max={max} onChange={(e)=>onChange(e.target.value||'')} style={{ position:'absolute', inset:0, opacity:0, width:'100%', height:'100%', cursor:'pointer', zIndex:10 }} />
      </Box>
    );
  };

  // Determine what to display based on cached pages
  const currentPage = pages[pageIndex];
  const displayEvents = currentPage?.events || events;
  const displayNextCursor = currentPage?.nextCursor || nextCursor;
  const canGoPrev = pageIndex > 0;
  const canGoNext = (!!displayNextCursor && hasMore) || (pageIndex < pages.length - 1);

  return (
    <Layout>
      <Box sx={{ mt: 0, px: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Shopify App History</Typography>
          {/* <Typography variant="body2" sx={{ color: '#6b7280' }}>Manage and view app install/uninstall events</Typography> */}
        </Box>

        {/*
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <UiDateField label="Start Date" value={dateRange.startDate} onChange={(val)=>setDateRange(r=>({...r,startDate:val}))} max={dateRange.endDate || undefined} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <UiDateField label="End Date" value={dateRange.endDate} onChange={(val)=>setDateRange(r=>({...r,endDate:val}))} min={dateRange.startDate || undefined} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 6 }}>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', md: 'row' } }}>
                <Button variant="contained" onClick={handleRefresh} size="medium" sx={{ bgcolor: '#111827', textTransform:'none', '&:hover': { bgcolor: '#0b1220' } }}>Filter</Button>
                <Button variant="outlined" onClick={() => { setDateRange({ startDate: today, endDate: today }); setCursor(''); dispatch(fetchAppEvents({ limit: 100, cursor: '', startDate: today, endDate: today })); dispatch(fetchAppEventsAggregate({ startDate: today, endDate: today })); }} size="medium" sx={{ borderColor:'#111827', color:'#111827', textTransform:'none', '&:hover':{ borderColor:'#0b1220', backgroundColor:'#f9fafb' } }}>Reset</Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
        */}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color:'#6b7280' }}>Total Installs</Typography>
              {aggregateLoading ? (
                <Box sx={{ display:'flex', alignItems:'center', gap:1, py:1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ color:'#6b7280' }}>Loading...</Typography>
                </Box>
              ) : (
                <Typography variant="h5" sx={{ fontWeight: 600 }}>{totalInstalls}</Typography>
              )}
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color:'#6b7280' }}>Total Uninstalls</Typography>
              {aggregateLoading ? (
                <Box sx={{ display:'flex', alignItems:'center', gap:1, py:1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ color:'#6b7280' }}>Loading...</Typography>
                </Box>
              ) : (
                <Typography variant="h5" sx={{ fontWeight: 600 }}>{totalUninstalls}</Typography>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 12 }}>
            <TableContainer component={Paper} sx={{ overflowX:'auto' }}>
              <Table stickyHeader sx={{ tableLayout:'fixed', minWidth:720, '& td, & th': { px:{ xs:1, sm:2 }, py:{ xs:0.75, sm:1 } } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight:600, bgcolor:'#f8fafc', py:1, minWidth:70, maxWidth:70, width:70 }}>S. No</TableCell>
                    <TableCell sx={{ fontWeight:600, bgcolor:'#f8fafc', py:1, minWidth:250, maxWidth:250, width:250 }}>Event</TableCell>
                    <TableCell sx={{ fontWeight:600, bgcolor:'#f8fafc', py:1, minWidth:160, maxWidth:185, width:165 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight:600, bgcolor:'#f8fafc', py:1, minWidth:160, maxWidth:200, width:180 }}>Shop Name</TableCell>
                    <TableCell sx={{ fontWeight:600, bgcolor:'#f8fafc', py:1, minWidth:220, maxWidth:260, width:240, display:{ xs:'none', md:'table-cell' } }}>myshopify Domain</TableCell>
                    <TableCell sx={{ fontWeight:600, bgcolor:'#f8fafc', py:1, minWidth:220, maxWidth:260, width:240, display:{ xs:'none', md:'table-cell' } }}>Store URL</TableCell>
                    {/* <TableCell sx={{ fontWeight:600, bgcolor:'#f8fafc', py:1, minWidth:180, maxWidth:200, width:190 }}>Created At</TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign:'center', py:8, height:300 }}>
                        <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                          <CircularProgress size={40} sx={{ mb:2 }} />
                          <Typography variant="body1" sx={{ color:'#6b7280' }}>Loading events...</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : Array.isArray(displayEvents) && displayEvents.length > 0 ? (
                    displayEvents.map((evt, index) => (
                      <TableRow key={index} hover sx={{ '&:hover': { backgroundColor:'#f8fafc' } }}>
                        <TableCell sx={{ py:1 }}><Typography variant="body2" sx={{ color:'#6b7280' }}>{(pageIndex * limit) + index + 1}</Typography></TableCell>
                        <TableCell sx={{ py:1 }}><Typography variant="body2" sx={{ color:'#374151', fontWeight:500 }}>{evt?.type || evt?.eventType || '—'}</Typography></TableCell>
                        <TableCell sx={{ py:1 }}>
                          {(() => {
                            const eventType = evt?.type || evt?.eventType || '';
                            const installed = eventType === 'RELATIONSHIP_INSTALLED';
                            return (
                              <Chip size="small" label={installed ? 'Installed' : 'Uninstalled'} color={installed ? 'success' : 'error'} variant={installed ? 'filled' : 'outlined'} />
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ py:1 }}>
                          <Typography variant="body2" sx={{ color:'#374151' }}>{evt?.shop?.name || evt?.shopName || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ py:1, display:{ xs:'none', md:'table-cell' } }}>
                          <Typography variant="body2" sx={{ color:'#6b7280' }}>{evt?.shop?.myshopifyDomain || evt?.myshopifyDomain || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ py:1, display:{ xs:'none', md:'table-cell' } }}><Typography variant="body2" sx={{ color:'#6b7280' }}>{evt?.shop?.domain || evt?.shopDomain || evt?.storeUrl || '—'}</Typography></TableCell>
                        {/* <TableCell sx={{ py:1 }}><Typography variant="body2" sx={{ color:'#6b7280' }}>{formatReadableDateTime(evt?.occurredAt || evt?.createdAt)}</Typography></TableCell> */}
                      </TableRow>
                    ))
                  ) : (
                  <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign:'center', py:4 }}>
                        <Typography variant="body2" sx={{ color:'#9ca3af' }}>No events</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display:'flex', justifyContent:'flex-end', alignItems:'center', gap:1, mt:2 }}>
              <IconButton size="small" onClick={handlePrevPage} disabled={!canGoPrev || listLoading} sx={{ color:'#111827', '&.Mui-disabled': { color:'#9ca3af' } }}>
                <ChevronLeftIcon />
              </IconButton>
              <IconButton size="small" onClick={handleNextPage} disabled={!canGoNext || listLoading} sx={{ color:'#111827', '&.Mui-disabled': { color:'#9ca3af' } }}>
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        {/* Debug aggregate payload (collapsible area could be added if desired) */}
        <Box sx={{ display:'none' }}>
          {aggregateError && <Typography variant="body2" color="error">{String(aggregateError)}</Typography>}
          <pre>{aggregatePretty}</pre>
        </Box>
      </Box>
    </Layout>
  );
}


